import React from 'react';

import { T } from '../customer/customers.theme';
import {
  ACCEPTED_TYPES,
  LOGO_FOLDER,
  MAX_INPUT_BYTES,
  uploadMenuImage,
} from '../menu/uploadMenuImage';
import { COPY } from './settings.copy';
import { DEFAULT_LOGO_SIZE, MAX_LOGO_SIZE, MIN_LOGO_SIZE } from './settings.rules';
import {
  Card,
  Field,
  FieldError,
  Hint,
  SectionHead,
  SmallButton,
  Toggle,
  TYPE,
} from './SettingsUI';

/**
 * The restaurant's picture, and how big it is drawn.
 *
 * ── Where the file actually goes ─────────────────────────────────────────
 *
 * Straight to the same Firebase Storage bucket the menu pictures live in,
 * through the same uploader, into a folder of its own. The API never sees the
 * file — `logoUrl` is a string on the business record, exactly as `imageUrl`
 * is a string on a menu item, so a picture set here is readable from the phone
 * without either side knowing about the other.
 *
 * The upload happens the moment a file is chosen, but `logoUrl` is only
 * written to the business when Save is pressed. That is deliberate: a picture
 * has to exist somewhere before it can be previewed, and a manager who picks
 * the wrong one should be able to press Discard. The cost is an orphaned
 * object in the bucket when they do — a few kilobytes, against a preview that
 * would otherwise be a lie.
 *
 * ── The size boxes ───────────────────────────────────────────────────────
 *
 * `logoWidth` and `logoHeight` are the space the picture is given, not a crop.
 * The preview draws it the same way anything sensible will — inside a box of
 * exactly those pixels, keeping its own proportions — so what is on screen is
 * what comes out.
 */

export default function LogoCard({
  form,
  errors,
  showErrors,
  busy,
  onChange,
  onNotice,
}) {
  const fileRef = React.useRef(null);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [uploadError, setUploadError] = React.useState('');

  // Logos are square far more often than not, and a manager setting one of the
  // two boxes almost never means "squash it".
  const [square, setSquare] = React.useState(
    () => !form.logoWidth || !form.logoHeight || form.logoWidth === form.logoHeight
  );

  const hasLogo = Boolean(String(form.logoUrl || '').trim());
  const locked = busy || uploading;

  const setSize = (field, value) => {
    const digits = value.replace(/[^0-9]/g, '').slice(0, 3);
    if (square) onChange({ logoWidth: digits, logoHeight: digits });
    else onChange({ [field]: digits });
  };

  const pick = () => {
    if (locked) return;
    setUploadError('');
    if (fileRef.current) fileRef.current.click();
  };

  const chosen = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Cleared straight away, so picking the same file twice still fires.
    event.target.value = '';
    if (!file) return;

    if (file.size > MAX_INPUT_BYTES) {
      setUploadError(COPY.logoTooBig);
      return;
    }

    setUploading(true);
    setProgress(0);
    setUploadError('');

    try {
      const url = await uploadMenuImage(file, {
        folder: LOGO_FOLDER,
        onProgress: setProgress,
      });

      // A restaurant setting its first picture has no stored size, and a
      // blank box next to a picture reads as broken.
      const patch = { logoUrl: url };
      if (!form.logoWidth) patch.logoWidth = String(DEFAULT_LOGO_SIZE);
      if (!form.logoHeight) patch.logoHeight = String(DEFAULT_LOGO_SIZE);
      onChange(patch);

      if (onNotice) onNotice(COPY.logoUploaded, false);
    } catch (error) {
      setUploadError(error?.message || 'Could not upload that picture');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = () => {
    if (locked) return;
    setUploadError('');
    // The sizes are left alone: someone removing a picture to put a better one
    // up should not have to set them again.
    onChange({ logoUrl: '' });
    if (onNotice) onNotice(COPY.logoRemoved, false);
  };

  const width = Number(form.logoWidth) || DEFAULT_LOGO_SIZE;
  const height = Number(form.logoHeight) || DEFAULT_LOGO_SIZE;
  const boxW = Math.min(Math.max(width, MIN_LOGO_SIZE), MAX_LOGO_SIZE);
  const boxH = Math.min(Math.max(height, MIN_LOGO_SIZE), MAX_LOGO_SIZE);

  return (
    <Card>
      <SectionHead title={COPY.logoTitle} body={COPY.logoBody} />

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_TYPES}
        onChange={chosen}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* the picture, at the size it is being given */}
        <div style={{ flex: 'none' }}>
          <div
            data-testid="logo-box"
            style={{
              width: boxW,
              height: boxH,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: T.sunk,
              border: `1px ${hasLogo ? 'solid' : 'dashed'} ${T.borderStrong}`,
              borderRadius: T.radiusControl,
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            {hasLogo ? (
              <img
                src={form.logoUrl}
                alt=""
                style={{
                  // Fills the box rather than sitting at its natural size, so
                  // a small file blown up to 200px looks as soft here as it
                  // will on the receipt. `contain` keeps it from distorting.
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            ) : (
              <span
                style={{
                  fontSize: TYPE.hint,
                  color: T.inkFaint,
                  textAlign: 'center',
                  padding: 8,
                }}
              >
                {uploading ? `${progress}%` : COPY.logoEmpty}
              </span>
            )}
          </div>
        </div>

        {/* what can be done to it */}
        <div style={{ flex: '1 1 300px', minWidth: 260 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <SmallButton onClick={pick} disabled={locked}>
              {uploading
                ? COPY.logoUploading
                : hasLogo
                ? COPY.logoReplace
                : COPY.logoChoose}
            </SmallButton>

            {hasLogo ? (
              <SmallButton onClick={remove} disabled={locked} tone="danger">
                {COPY.logoRemove}
              </SmallButton>
            ) : null}
          </div>

          <Hint>{COPY.logoAccepted}</Hint>
          <FieldError>{uploadError}</FieldError>

          {hasLogo ? (
            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: '0 0 116px', minWidth: 0 }}>
                  <Field
                    label={COPY.logoWidth}
                    name="settings-logo-width"
                    value={form.logoWidth}
                    onChange={(value) => setSize('logoWidth', value)}
                    inputMode="numeric"
                    suffix="px"
                    disabled={locked}
                    invalid={showErrors && Boolean(errors.logoWidth)}
                  />
                </div>
                <div style={{ flex: '0 0 116px', minWidth: 0 }}>
                  <Field
                    label={COPY.logoHeight}
                    name="settings-logo-height"
                    value={form.logoHeight}
                    onChange={(value) => setSize('logoHeight', value)}
                    inputMode="numeric"
                    suffix="px"
                    disabled={locked || square}
                    invalid={showErrors && Boolean(errors.logoHeight)}
                  />
                </div>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginTop: 25,
                    fontSize: TYPE.label,
                    fontWeight: 600,
                    color: T.ink,
                    cursor: locked ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Toggle
                    checked={square}
                    disabled={locked}
                    label={COPY.logoLock}
                    onChange={(next) => {
                      setSquare(next);
                      if (next) onChange({ logoHeight: form.logoWidth });
                    }}
                  />
                  <span>{COPY.logoLock}</span>
                </label>
              </div>

              <FieldError>
                {showErrors ? errors.logoWidth || errors.logoHeight : ''}
              </FieldError>
              <Hint>
                {COPY.logoSizeHint(boxW, boxH)} {COPY.logoSizeNote}
              </Hint>
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
